/**
 * The org chart and product tree versions an object was made under (migration
 * 929; cycle reports and Plan of Record snapshots since 730), with every
 * version of both trees so a live object can say "now vN".
 *
 * One read per object row, and the workspace's version history cached per
 * workspace for the session.
 */

import { useEffect, useState } from 'react';
import { getSupabaseClient } from '@/lib/supabaseClient';
import { listStructureRevisions } from '@/lib/hierarchy/structureRevisionsService';
import type { StructureRevision } from '@/lib/hierarchy/structureRevisions';

/** The tables that carry org_structure_revision_id / product_structure_revision_id. */
export type PinnedTable =
  | 'items'
  | 'degap_cycles'
  | 'tracks'
  | 'prioritization_sessions'
  | 'session_results'
  | 'priority_sets'
  | 'item_pools'
  | 'plan_cycles'
  | 'cycle_reports';

export interface StructurePins {
  workspaceId: string;
  org: StructureRevision | null;
  product: StructureRevision | null;
  /** The tree's version in force now; null when the workspace has none recorded. */
  orgNow: StructureRevision | null;
  productNow: StructureRevision | null;
}

const history = new Map<string, Promise<{ org: StructureRevision[]; product: StructureRevision[] }>>();

function revisionsFor(workspaceId: string) {
  let p = history.get(workspaceId);
  if (!p) {
    p = Promise.all([
      listStructureRevisions(workspaceId, 'org').catch(() => []),
      listStructureRevisions(workspaceId, 'product').catch(() => []),
    ]).then(([org, product]) => ({ org, product }));
    history.set(workspaceId, p);
  }
  return p;
}

/**
 * Pins for one row, found by `column = value` (the row's id unless said
 * otherwise; the newest row when several match). null while loading or when
 * nothing matches.
 */
export function useStructurePins(table: PinnedTable, value: string | null | undefined, column = 'id'): StructurePins | null {
  const [pins, setPins] = useState<StructurePins | null>(null);
  useEffect(() => {
    if (!value) {
      setPins(null);
      return;
    }
    let live = true;
    (async () => {
      // These columns arrive with migration 929 and are not in the generated types yet.
      const { data } = await getSupabaseClient()
        .from(table)
        .select('workspace_id, org_structure_revision_id, product_structure_revision_id, created_at')
        .eq(column, value)
        .order('created_at', { ascending: false })
        .limit(1);
      const row = (data?.[0] ?? null) as {
        workspace_id: string | null;
        org_structure_revision_id: string | null;
        product_structure_revision_id: string | null;
      } | null;
      if (!row?.workspace_id) {
        if (live) setPins(null);
        return;
      }
      const revs = await revisionsFor(row.workspace_id);
      if (!live) return;
      const now = (list: StructureRevision[]) => list.find((r) => r.status === 'authorized') ?? null;
      setPins({
        workspaceId: row.workspace_id,
        org: revs.org.find((r) => r.id === row.org_structure_revision_id) ?? null,
        product: revs.product.find((r) => r.id === row.product_structure_revision_id) ?? null,
        orgNow: now(revs.org),
        productNow: now(revs.product),
      });
    })().catch(() => live && setPins(null));
    return () => {
      live = false;
    };
  }, [table, value, column]);
  return pins;
}
